import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Divider, Fab, ListItemText, MenuItem, MenuList, useMediaQuery, useTheme, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterIcon from "@mui/icons-material/FilterList";
import { useDebounce } from "use-debounce";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { useAppPaths } from "../../Routes";
import { IconButtonMenu, MenuCheckboxGroup } from "../Menu";
import SearchBox from "../SearchBox";
import DeletableChips from "../DeletableChips";
import UsersTable from "./UsersTable";
import useIsLargeScreen from "../../hooks/useIsLargeScreen";

const UsersTableWithSearch = () => {
    const appPaths = useAppPaths();
    const theme = useTheme();
    const isLargeScreen = useIsLargeScreen();
    const showFilterChipsInline = useMediaQuery("(min-width: 800px)");
    const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem("searchTerm") || "");
    const [includeAdmins, setIncludeAdmins] = useState(() => sessionStorage.getItem("includeAdmins") === "true");
    const [includeNonAdmins, setIncludeNonAdmins] = useState(() => sessionStorage.getItem("includeNonAdmins") === "true");
    const [includeArchived, setIncludeArchived] = useState(() => sessionStorage.getItem("includeArchived") === "true");
    const [includeActive, setIncludeActive] = useState(() => sessionStorage.getItem("includeActive") === "true");
    const [includeClients, setIncludeClients] = useState(() => sessionStorage.getItem("includeClients") === "true");
    const [includeTalents, setIncludeTalents] = useState(() => sessionStorage.getItem("includeTalents") === "true");
    const [debounceSearchTerm] = useDebounce(searchTerm, 700);

    const isAdminFilter = includeAdmins === includeNonAdmins ? undefined : includeAdmins;
    const isArchivedFilter = includeArchived === includeActive ? undefined : includeArchived;

    useEffect(() => {
        sessionStorage.setItem("searchTerm", searchTerm);
    }, [searchTerm]);
    
    useEffect(() => {
        sessionStorage.setItem("includeAdmins", includeAdmins.toString());
    }, [includeAdmins]);
    
    useEffect(() => {
        sessionStorage.setItem("includeNonAdmins", includeNonAdmins.toString());
    }, [includeNonAdmins]);
    
    useEffect(() => {
        sessionStorage.setItem("includeArchived", includeArchived.toString());
    }, [includeArchived]);
    
    useEffect(() => {
        sessionStorage.setItem("includeActive", includeActive.toString());
    }, [includeActive]);
    
    useEffect(() => {
        sessionStorage.setItem("includeClients", includeClients.toString());
    }, [includeClients]);
    
    useEffect(() => {
        sessionStorage.setItem("includeTalents", includeTalents.toString());
    }, [includeTalents]);

    const filterChips = (
        <DeletableChips
            chips={[
                {
                    label: "Admin",
                    onDelete: () => setIncludeAdmins(false),
                    visible: includeAdmins,
                },
                {
                    label: "Non-admin",
                    onDelete: () => setIncludeNonAdmins(false),
                    visible: includeNonAdmins,
                },
                {
                    label: "Archived",
                    onDelete: () => setIncludeArchived(false),
                    visible: includeArchived,
                },
                {
                    label: "Active",
                    onDelete: () => setIncludeActive(false),
                    visible: includeActive,
                },
                {
                    label: "Client",
                    onDelete: () => setIncludeClients(false),
                    visible: includeClients,
                },
                {
                    label: "Talent",
                    onDelete: () => setIncludeTalents(false),
                    visible: includeTalents,
                },
            ]}
            sx={{
                color: `${theme.palette.tertiary.main} !important`,
                backgroundColor: `${theme.palette.primary.dark} !important`,
            }}
            deleteIcon={<CloseRoundedIcon />}
        />
    );

    return (
        <>
            <Box sx={{
                display: "flex",
                alignItems: "center",
                marginTop: theme.spacing(3),
                "> *:not(:first-of-type)": {
                    marginLeft: 1,
                },
            }}>
                <SearchBox
                    placeholder={isLargeScreen ? "Search by First name, Last name or Email" : "Search Users"}
                    onChange={setSearchTerm}
                    searchTerm={searchTerm}
                    sx={showFilterChipsInline ? { minWidth: "28rem", background: "#FFFFFF" } : { flexGrow: 1, background: "#FFFFFF" }}
                />
                {showFilterChipsInline && (
                    <>
                        {filterChips}
                        <Box sx={{ flexGrow: 1 }} />
                    </>
                )}
                <IconButtonMenu
                    id="filter-users"
                    icon={isLargeScreen ? <FilterIcon sx={{ color: theme.palette.secondary.main }} fontSize="large" /> : <MoreVertIcon sx={{ color: theme.palette.secondary.main }} fontSize="large" />}

                >
                    <MenuList sx={{ width: "17rem", padding: 0 }}>
                        <MenuCheckboxGroup
                            label="Is admin"
                            options={[
                                {
                                    label: "Yes",
                                    value: includeAdmins,
                                    onChange: setIncludeAdmins,
                                },
                                {
                                    label: "No",
                                    value: includeNonAdmins,
                                    onChange: setIncludeNonAdmins,
                                },
                            ]}
                        />
                        <Divider />
                        <MenuCheckboxGroup
                            label="User type"
                            options={[
                                {
                                    label: "Client",
                                    value: includeClients,
                                    onChange: setIncludeClients
                                },
                                {
                                    label: "Talent",
                                    value: includeTalents,
                                    onChange: setIncludeTalents
                                }
                            ]}
                        />
                        <Divider />
                        <MenuCheckboxGroup
                            label="Is active"
                            options={[
                                {
                                    label: "Yes",
                                    value: includeActive,
                                    onChange: setIncludeActive,
                                },
                                {
                                    label: "No",
                                    value: includeArchived,
                                    onChange: setIncludeArchived,
                                },
                            ]}
                        />
                        <Divider />
                        <MenuItem
                            onClick={() => {
                                setIncludeAdmins(false);
                                setIncludeNonAdmins(false);
                                setIncludeArchived(false);
                                setIncludeActive(false);
                                setIncludeClients(false);
                                setIncludeTalents(false);
                                sessionStorage.removeItem("includeAdmins");
                                sessionStorage.removeItem("includeNonAdmins");
                                sessionStorage.removeItem("includeArchived");
                                sessionStorage.removeItem("includeActive");
                                sessionStorage.removeItem("includeClients");
                                sessionStorage.removeItem("includeTalents");
                            }}
                        >
                            <ListItemText sx={{ textAlign: "right" }}>
                                Clear filters
                            </ListItemText>
                        </MenuItem>
                    </MenuList>
                </IconButtonMenu>

                {isLargeScreen && (
                    <Link to={appPaths.users.create} style={{ textDecoration: "none", outline: "none", marginLeft: "1.25rem" }}>
                        <Button
                            size="medium"
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                        >
                            Create
                        </Button>
                    </Link>
                )}
            </Box>
            {!showFilterChipsInline && (
                <Box sx={{
                    marginTop: "1.5rem"
                }}>
                    {filterChips}
                </Box>
            )}
            <Box sx={{ marginTop: "1.5rem" }}>
                <UsersTable
                    searchTerm={debounceSearchTerm}
                    isAdminFilter={isAdminFilter}
                    isArchivedFilter={isArchivedFilter}
                    isClientFilter={includeClients}
                    isTalentFilter={includeTalents}
                />
            </Box>
            {!isLargeScreen && (
                <Link to={appPaths.users.create}>
                    <Fab
                        color="primary"
                        aria-label="create-user"
                        sx={{
                            position: "fixed",
                            bottom: theme.spacing(10),
                            right: theme.spacing(4),
                        }}
                    >
                        <AddIcon fontSize="large" />
                    </Fab>
                </Link>
            )}
        </>
    );
};

export default UsersTableWithSearch;